/**
 * Hash 模式下（如 #/h5/xxx）React Router 的 pathname 有时仍为 "/"，
 * 真实页面路径需结合 window.location.hash 判断。
 */
export function getEffectiveAppPath(pathname: string): string {
  if (pathname.startsWith("/h5")) return pathname;
  if (typeof window === "undefined") return pathname;
  const hashPath = (window.location.hash || "").replace(/^#/, "").split(/[?#]/)[0];
  return hashPath.startsWith("/h5") ? hashPath : pathname;
}

export function isH5AppRoute(pathname: string): boolean {
  return getEffectiveAppPath(pathname).startsWith("/h5");
}

/** 从 CommonSidebar 进入业务应用时标记，AuthLayout 据此保留应用视角侧栏 */
export const APPLICATION_SIDEBAR_CONTEXT_KEY = "application_sidebar_context";

export function setApplicationSidebarContext(active: boolean): void {
  if (typeof window === "undefined") return;
  if (active) {
    window.sessionStorage.setItem(APPLICATION_SIDEBAR_CONTEXT_KEY, "1");
  } else {
    window.sessionStorage.removeItem(APPLICATION_SIDEBAR_CONTEXT_KEY);
  }
}

export function hasApplicationSidebarContext(): boolean {
  if (typeof window === "undefined") return false;
  return window.sessionStorage.getItem(APPLICATION_SIDEBAR_CONTEXT_KEY) === "1";
}

export const noAuthLayoutPaths = [
  "/wx-browser", // 微信浏览器打开的提示页
  "/invite-friend", // 邀请好友页
  "/gjt-auth-login", // 桂教通登录页
  "/wecom-auth-login", // 企业微信登录页
  "/wecom-logout",
  "/login", // 登录页
  "/register", // 注册页
  "/school/invite", // 邀请链接详情页
  "/membership", // 会员订购页
  "/membership/checkout", // 会员结算页
  "/activity/voucher-claim", // Web 老客户代金券领取页（公开落地页跳入，无需登录）
  "/cowork-payment", // 联研共创付款确认页
  "/cowork-entry", // 共创办理码入口
  "/school/co-creation/application/detail", // 共创申请免登录付款页（兼容旧链接）
  "/teacher-home", // 教师首页（支持非登录态）
  "/user/profile-new", // 新版应用中心（支持非登录态浏览）
  "/basejw/appcenter", // 新版应用中心独立公开路由
  "/basejw/kuaijw-consultation", // 快教务试用3天申请页（公开落地页，面向无账号访客）
  "/basejw/apphome/style", // 应用中心风格方案实验路由（公开预览）
  "/my-apps", // 我的应用页面（使用 CommonNavHeader 和 CommonSidebar）
  "/course-class-roster-poster", // 课程班更新名单活动海报页（支持分享访问）
  "/school-info-new", // 基础信息管理（应用视角，使用新版应用壳）
  "/calendar-app", // 校历（应用视角，使用新版应用壳）
  "/timetable-app", // 课表管理（应用视角，使用 CommonNavHeader 和 CommonSidebar）
  "/gaokao-subject-choice-app", // 新高考选科一指通（独立应用，无导航）
  "/gaokao-subject-choice-app-new", // 新高考选科一指通（新版本，使用 CommonNavHeader 和 CommonSidebar）
  "/gaokao-invigilation-app", // 高中智监考（应用视角，使用新版应用壳）
  "/gaokao-course-management-app-new", // 高中智课表（新版本，使用 CommonNavHeader 和 CommonSidebar）
  "/score-card-app-new", // 高中智成绩（新版本，使用 CommonNavHeader 和 CommonSidebar）
  "/smart-division-app", // 智能分班应用（使用 CommonNavHeader 和 CommonSidebar）
  "/intl-school-info-app", // 国际通基础信息管理
  "/intl-calendar-app", // 国际通校历
  "/intl-subject-choice-app", // 国际通选课
  "/intl-smart-division-legacy-app", // 国际通分班
  "/intl-smart-division-app", // 国际通分班
  "/intl-smart-scheduling-app", // 国际通排课
  "/intl-course-management-app", // 国际通课表
  "/intl-invigilation-app", // 国际通监考
  "/intl-score-card-app", // 国际通成绩
  "/intl-admission-app", // 国际入学通
  "/smart-division-intro", // 高中智能分班介绍页（静态页面，无需登录）
  "/newgaokaosubjectselection", // 新高考选科介绍页（静态页面，无需登录）
  "/cross-login-debug", // 跨系统登录调试页面
  "/cross-login-token", // 跨易通 token 中转页（拉取 token 后跳转目标系统）
  "/scenario", // 场景活动页（支持非登录态浏览大促活动）
  "/user/wallet-withdrawal-preview", // 用户侧钱包提现前端模拟页
];

export const courseSchedulingPaths = [
  "/input-information", // 录入信息
  "/arrange-teaching", // 课时任课
  "/setting-rules", // 设置条件
  "/adjust-schedule", // 排课调课
  "/preview-export", // 预览导出
  "/schedule-release", // 课表发布
  "/adjust-substitute", // 调代课
  "/course-hour-stat", // 课时统计
  "/adjust-stat", // 调代课&课时统计入口页
  "/published-timetable", // 已发布课表
];

export const h5NoAuthLayoutPaths = [
  "/h5/login", // H5 默认登录页（选择页面）
  "/h5/login/teacher", // H5 教师登录页
  "/h5/login/parent", // H5 家长登录页
  "/h5/invite-login", // H5 邀请码登录页
  "/h5/invite-code-input", // H5 邀请码输入页
  "/h5/teacher-home", // H5 教师首页（支持非登录态）
  "/h5/membership", // H5 会员订购页
  "/h5/membership/checkout", // H5 会员结算页
  "/cowork-payment", // 联研共创付款确认页
  "/cowork-entry", // 共创办理码入口
  "/school/co-creation/application/detail", // 共创申请免登录付款页（兼容旧链接）
  "/h5/newgaokaosubjectselection", // H5 新高考选科介绍页（静态页面，无需登录）
  "/h5/activity/zhixuanke-h5camp", // H5 智选科活动体验页（静态页面，无需登录）
  "/h5/activity/zhichengji-h5camp", // H5 倍思智成绩活动体验页（静态页面，无需登录）
  "/h5/activity/zhifenban-h5camp", // H5 智分班活动体验页（静态页面，无需登录）
  "/h5/timetable-share", // H5 总课表分享页（任何人通过链接/二维码可进入并填写修改建议，无需登录）
  "/h5/teacher-hours-share", // H5 教师周课时明细分享页（同上，无需登录）
  "/h5/activity-share", // H5 活动分享页（无需登录）
  "/h5/activity/august-campaign", // H5 8月大促活动页（从basejy-land公开落地页跳入，无需登录）
  "/h5/activity/voucher-claim", // H5 老客户代金券领取页（公开落地页跳入，无需登录）
  "/membership", // 会员订购页（支持非登录态，H5 和 Web 共用）
  "/membership/checkout", // 会员结算页
];

function matchesLayoutPath(paths: string[], pathname: string): boolean {
  return paths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export function isNoAuthLayoutPath(pathname: string): boolean {
  const effectivePath = getEffectiveAppPath(pathname);
  return (
    matchesLayoutPath(noAuthLayoutPaths, effectivePath) ||
    matchesLayoutPath(h5NoAuthLayoutPaths, effectivePath)
  );
}

export const gjtAuthPaths = [...courseSchedulingPaths, "/team"];
